// SCADA design-system kit — base building blocks for all dispatcher screens.
// Dark-first, styled with inline tokens (matches cabinet conventions).
import type { ReactNode } from 'react';
import CircularGauge from '@/components/base/CircularGauge';
import {
  scada, STATE_LABEL, stateColor, qualityColor,
  type OpState, type Quality,
} from '@/scada/tokens';

// ─── Panel container ─────────────────────────────────────────────────────────
export function SectionCard({
  title, subtitle, right, children, padded = true, fill = false,
}: { title?: string; subtitle?: string; right?: ReactNode; children: ReactNode; padded?: boolean; fill?: boolean }) {
  return (
    <div style={{ background: scada.panel, border: `1px solid ${scada.border}`, borderRadius: 12, overflow: 'hidden', height: fill ? '100%' : undefined, display: fill ? 'flex' : undefined, flexDirection: fill ? 'column' : undefined }}>
      {(title || right) && (
        <div className="flex items-center justify-between"
          style={{ padding: '10px 14px', background: scada.panel2, borderBottom: `1px solid ${scada.border}` }}>
          <div>
            {title && <div style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{title}</div>}
            {subtitle && <div style={{ color: scada.textMuted, fontSize: 11 }}>{subtitle}</div>}
          </div>
          {right}
        </div>
      )}
      <div style={{ padding: padded ? 14 : 0, flex: fill ? 1 : undefined, display: fill ? 'flex' : undefined, flexDirection: fill ? 'column' : undefined, justifyContent: fill ? 'center' : undefined }}>{children}</div>
    </div>
  );
}

// ─── Status badge ────────────────────────────────────────────────────────────
export function StatusBadge({ state, small }: { state: OpState; small?: boolean }) {
  const c = stateColor(state);
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap"
      style={{
        background: `${c}1A`, color: c, border: `1px solid ${c}40`,
        borderRadius: 999, padding: small ? '1px 8px' : '3px 10px',
        fontSize: small ? 10 : 11, fontWeight: 600,
      }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: c, display: 'inline-block' }} />
      {STATE_LABEL[state]}
    </span>
  );
}

// ─── Quality dot ─────────────────────────────────────────────────────────────
export function QualityDot({ quality, label }: { quality: Quality; label?: boolean }) {
  const c = qualityColor(quality);
  return (
    <span className="inline-flex items-center gap-1" title={`Якість даних: ${quality}`}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: c, display: 'inline-block' }} />
      {label && <span style={{ color: scada.textMuted, fontSize: 10 }}>{quality}</span>}
    </span>
  );
}

// ─── Metric tile ─────────────────────────────────────────────────────────────
export function MetricTile({
  label, value, unit, quality, accent,
}: { label: string; value: ReactNode; unit?: string; quality?: Quality; accent?: string }) {
  return (
    <div style={{ background: scada.panel2, border: `1px solid ${scada.border}`, borderRadius: 10, padding: '10px 12px' }}>
      <div className="flex items-center justify-between">
        <span style={{ color: scada.textMuted, fontSize: 11 }}>{label}</span>
        {quality && <QualityDot quality={quality} />}
      </div>
      <div className="flex items-baseline gap-1" style={{ marginTop: 4 }}>
        <span style={{ color: accent ?? scada.text, fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>{value}</span>
        {unit && <span style={{ color: scada.textDim, fontSize: 12 }}>{unit}</span>}
      </div>
    </div>
  );
}

// ─── Power gauge (wraps base CircularGauge) ──────────────────────────────────
export function PowerGauge({
  valueKw, maxKw, size = 150, color = scada.state.ok, label = 'Активна потужність',
}: { valueKw: number; maxKw: number; size?: number; color?: string; label?: string }) {
  return (
    <CircularGauge value={valueKw} max={maxKw} size={size} strokeWidth={10}
      color={color} trackColor={scada.panel2}>
      <span style={{ color: scada.text, fontSize: 24, fontWeight: 700 }}>
        {valueKw >= 1000 ? (valueKw / 1000).toFixed(2) : Math.round(valueKw)}
      </span>
      <span style={{ color: scada.textDim, fontSize: 12 }}>{valueKw >= 1000 ? 'МВт' : 'кВт'}</span>
      <span style={{ color: scada.textMuted, fontSize: 10, marginTop: 2, textAlign: 'center' }}>{label}</span>
    </CircularGauge>
  );
}

// ─── SBO confirm modal (Select Before Operate, per ATS/IEC pattern) ──────────
export function SboConfirmModal({
  open, title, current, next, unit, warning, onConfirm, onCancel,
}: {
  open: boolean; title: string;
  current: string | number; next: string | number; unit?: string;
  warning?: string; onConfirm: () => void; onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div style={{ background: scada.panel, border: `1px solid ${scada.borderStrong}`, borderRadius: 14, width: 420, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', background: scada.panel2, borderBottom: `1px solid ${scada.border}`, color: scada.text, fontWeight: 600, fontSize: 14 }}>
          <i className="ri-shield-check-line" style={{ marginRight: 8, color: scada.accent }} />{title}
        </div>
        <div style={{ padding: 16 }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <div className="text-center" style={{ flex: 1 }}>
              <div style={{ color: scada.textMuted, fontSize: 11 }}>Поточне</div>
              <div style={{ color: scada.textDim, fontSize: 22, fontWeight: 700 }}>{current}<span style={{ fontSize: 12 }}> {unit}</span></div>
            </div>
            <i className="ri-arrow-right-line" style={{ color: scada.textMuted, fontSize: 20, margin: '0 12px' }} />
            <div className="text-center" style={{ flex: 1 }}>
              <div style={{ color: scada.textMuted, fontSize: 11 }}>Нове</div>
              <div style={{ color: scada.state.curtailed, fontSize: 22, fontWeight: 700 }}>{next}<span style={{ fontSize: 12 }}> {unit}</span></div>
            </div>
          </div>
          {warning && (
            <div style={{ background: `${scada.state.warning}1A`, border: `1px solid ${scada.state.warning}40`, color: scada.state.warning, borderRadius: 8, padding: '8px 10px', fontSize: 12, marginBottom: 12 }}>
              <i className="ri-alert-line" style={{ marginRight: 6 }} />{warning}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={onCancel}
              style={{ background: 'transparent', border: `1px solid ${scada.border}`, color: scada.textDim, borderRadius: 8, padding: '7px 16px', fontSize: 13, cursor: 'pointer' }}>
              Скасувати
            </button>
            <button onClick={onConfirm}
              style={{ background: scada.accent, border: 'none', color: '#fff', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Підтвердити і виконати
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Screen stub (owner-friendly "coming soon" placeholder) ──────────────────
// `stage`/`tzRef` are accepted for call-site compatibility but NOT rendered —
// the UI shows no dev jargon (no stage codes, library names or TЗ references).
export function ScreenStub({
  title, children,
}: { title: string; stage?: string; tzRef?: string; children?: ReactNode }) {
  return (
    <SectionCard title={title}>
      <div className="flex items-center gap-2" style={{ marginBottom: children ? 14 : 0 }}>
        <span style={{ background: `${scada.state.curtailed}1A`, color: scada.state.curtailed, border: `1px solid ${scada.state.curtailed}40`, borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
          Скоро
        </span>
        <span style={{ color: scada.textMuted, fontSize: 12 }}>Розділ у підготовці</span>
      </div>
      {children}
    </SectionCard>
  );
}
